import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, X, Crown, Heart, ShoppingBag, ArrowUpRight,
  Coins, Zap, Plus, Gift, Receipt, RefreshCw, Star, Sparkles, ChevronRight
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import TransactionHistory from "./wallet/TransactionHistory";

const VIP_COLORS = {
  bronze:  "#cd7f32",
  gold:    "#f59e0b",
  diamond: "#00e5ff",
  none:    "#71717a"
};

export default function WalletPopover({ align = "left", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen]         = useState(false);
  const [view, setView]         = useState("main"); // main | history
  const [balance, setBalance]   = useState(null);
  const ref = useRef(null);

  const fetchBalance = useCallback(() => {
    if (!user) return;
    api.get("/wallet/balance")
      .then(r => setBalance(r.data))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (open) { fetchBalance(); setView("main"); }
  }, [open, fetchBalance]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (!user) return null;

  const points    = balance?.points    ?? user.points    ?? 0;
  const idgCoins  = balance?.idg_coins ?? user.idg_coins ?? 0;
  const vipTier   = balance?.vip_tier  ?? user.vip_tier  ?? "none";
  const vipUntil  = balance?.vip_until ?? user.vip_until;
  const isVip     = vipTier !== "none";
  const vipColor  = VIP_COLORS[vipTier] || VIP_COLORS.none;

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="wallet-popover">
      {/* ─── Header Top-Bar Button ─── */}
      <button
        onClick={() => setOpen(p => !p)}
        className={`h-7 px-2.5 rounded-[4px] flex items-center gap-2 border transition-all duration-200 hover:scale-105 ${
          open
            ? "bg-amber-500/20 border-amber-500/60 text-white shadow-[0_0_12px_rgba(251,191,36,0.3)]"
            : "bg-white/[0.04] hover:bg-white/[0.08] border-white/10 hover:border-amber-500/50 text-zinc-300 hover:text-white"
        }`}
        title="Portofel IDG"
        data-testid="wallet-btn"
      >
        <Wallet className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-[11px] font-chakra font-black tracking-wider uppercase leading-none">PORTOFEL</span>
        <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-sm leading-none">
          {idgCoins.toLocaleString()}
        </span>
        {isVip && (
          <Star className="w-3 h-3" style={{ color: vipColor }} />
        )}
      </button>

      {/* ─── Spacious Modern Popover Window (380px) ─── */}
      {open && (
        <div
          className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-2 w-[380px] bg-[#090b10] border border-white/15 rounded-xl shadow-[0_30px_90px_rgba(0,0,0,0.95)] z-[1000] overflow-hidden animate-scaleUp`}
          data-testid="wallet-dropdown"
        >
          {/* Top subtle glow edge */}
          <div className="h-[2px] w-full bg-gradient-to-r from-amber-500 via-[var(--tf-primary)] to-rose-500" />

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8 bg-white/[0.02]">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Wallet className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <span className="text-xs font-chakra font-black uppercase tracking-widest text-white block">
                  PORTOFEL & MONEDE
                </span>
                <span className="text-[10px] font-mono text-zinc-500">
                  {user.username}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button 
                onClick={fetchBalance} 
                title="Actualizează sold" 
                className="w-7 h-7 rounded bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors flex items-center justify-center"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={() => setOpen(false)} 
                className="w-7 h-7 rounded bg-white/5 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition-all"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {view === "main" ? (
            <div className="p-4 space-y-4">
              
              {/* ─── Integrated Balance Card ─── */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-white/[0.04] via-black/40 to-black/80 border border-white/10 space-y-3 shadow-inner">
                <div className="grid grid-cols-2 gap-3 divide-x divide-white/10">
                  
                  {/* Coins Balance */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] font-chakra font-black uppercase text-amber-400/90 tracking-wider">
                      <Coins className="w-3.5 h-3.5 text-amber-400" />
                      <span>IDG COINS</span>
                    </div>
                    <div className="font-mono text-2xl font-black text-amber-400 tracking-tight">
                      {idgCoins.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-mono text-zinc-500">Monedă magazin & VIP</div>
                  </div>

                  {/* Activity Points */}
                  <div className="pl-3 space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] font-chakra font-black uppercase text-cyan-400/90 tracking-wider">
                      <Zap className="w-3.5 h-3.5 text-cyan-400" />
                      <span>PUNCTE FORUM</span>
                    </div>
                    <div className="font-mono text-2xl font-black text-white tracking-tight">
                      {points.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-mono text-zinc-500">Scor activitate comună</div>
                  </div>

                </div>

                {/* VIP Status Bar */}
                <div className="pt-2.5 border-t border-white/5 flex items-center justify-between text-[11px] font-mono">
                  <span className="text-zinc-400 flex items-center gap-1.5">
                    <Crown className="w-3.5 h-3.5" style={{ color: vipColor }} />
                    <span>Statut Cont:</span>
                  </span>
                  <span className="font-bold font-chakra uppercase px-2 py-0.5 rounded text-[10px]" style={{ color: vipColor, backgroundColor: `${vipColor}15` }}>
                    {isVip ? `VIP ${vipTier.toUpperCase()}` : "Membru Standard"}
                  </span>
                </div>
              </div>

              {/* ─── Hero Action: Cumpără IDG Coins ─── */}
              <Link
                to="/donate?tab=recharge"
                onClick={() => setOpen(false)}
                className="w-full flex items-center justify-between p-3.5 rounded-xl bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent border border-amber-500/40 hover:border-amber-500 hover:from-amber-500/25 transition-all group shadow-[0_0_20px_rgba(245,158,11,0.1)]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shadow-sm">
                    <Plus className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-chakra font-black uppercase tracking-wider text-white group-hover:text-amber-400 transition-colors flex items-center gap-1.5">
                      <span>Cumpără IDG Coins</span>
                      <span className="text-[9px] font-mono bg-emerald-500/20 text-emerald-400 px-1.5 py-0.2 rounded">
                        INSTANT
                      </span>
                    </div>
                    <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                      Card · PayPal · Revolut · Crypto · Paysafe
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[11px] font-chakra font-black uppercase text-amber-400 shrink-0">
                  <span>DESCHIDE</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </Link>

              {/* ─── Secondary Quick Grid (2 Columns) ─── */}
              <div className="grid grid-cols-2 gap-2.5">
                
                {/* Buy VIP */}
                <Link
                  to="/donate?tab=vip"
                  onClick={() => setOpen(false)}
                  className="p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/10 hover:border-amber-500/40 transition-all group flex flex-col justify-between"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-amber-400 transition-colors" />
                  </div>
                  <div>
                    <div className="text-xs font-chakra font-bold uppercase text-white group-hover:text-amber-400 transition-colors">
                      Abonamente VIP
                    </div>
                    <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
                      Bronze / Gold / Diamond
                    </div>
                  </div>
                </Link>

                {/* Redeem Voucher */}
                <Link
                  to="/donate?tab=voucher"
                  onClick={() => setOpen(false)}
                  className="p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/10 hover:border-violet-500/40 transition-all group flex flex-col justify-between"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Gift className="w-4 h-4 text-violet-400" />
                    <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-violet-400 transition-colors" />
                  </div>
                  <div>
                    <div className="text-xs font-chakra font-bold uppercase text-white group-hover:text-violet-400 transition-colors">
                      Cod Voucher PIN
                    </div>
                    <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
                      Revendică monede
                    </div>
                  </div>
                </Link>

              </div>

              {/* ─── Tertiary History & Direct Links ─── */}
              <div className="pt-2 border-t border-white/8 flex items-center justify-between gap-2">
                <button
                  onClick={() => setView("history")}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-zinc-300 hover:text-white text-xs font-chakra font-bold uppercase transition-all"
                >
                  <Receipt className="w-3.5 h-3.5 text-zinc-400" />
                  <span>Tranzacții</span>
                </button>

                <Link
                  to="/loadout"
                  onClick={() => setOpen(false)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:text-cyan-300 text-xs font-chakra font-black uppercase transition-all"
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                  <span>Loadout</span>
                </Link>
              </div>

            </div>
          ) : (
            /* ─── History View ─── */
            <div className="p-4 space-y-3">
              <button 
                onClick={() => setView("main")} 
                className="text-xs font-chakra font-bold uppercase text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5"
              >
                ← Înapoi la Portofel
              </button>
              <div className="pt-2 border-t border-white/8">
                <TransactionHistory />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
